import { API } from '@/constant/constants'
import {
  DATA_PAGINATION,
  UPDATE_SOIL_MOISTURE_DATA,
  UPDATE_STATION_DATA,
} from '@/types'
import { ErrorCode, SearchParams } from '@/types/common'
import { OffStationMultiple } from '@/types/controller'
import { DecoderReading } from '@/types/decoder'
import type { ResetLineUnits } from '@/types/interface'
import {
  ApplyObservedFlow,
  CreateStation,
  STATION_DETAIL,
  SearchStation,
  SetupStation,
  SoilMoistureDetail,
  UpdateStation,
} from '@/types/station'
import { queryStringify } from '@/ultils'
import axiosClient from '../axiosClient'

const stationDataConfigApi = {
  getAll(searchParams: SearchStation) {
    const url = '/api/station'
    return axiosClient.get(url + queryStringify(searchParams))
  },
  getEtzone() {
    const url = '/api/etzone/getall'
    return axiosClient.get(url)
  },
  getById(interfaceId: string, id: string) {
    const url = `/api/station/${interfaceId}/${id}`
    return axiosClient.get<STATION_DETAIL>(url)
  },
  getErrorCode(interfaceId: string, errorCode: string) {
    const url = `/api/errorCode/${interfaceId}/${errorCode}`
    return axiosClient.get<ErrorCode>(url)
  },
  create(payload: CreateStation) {
    const url = '/api/station'
    return axiosClient.post(url, payload)
  },
  resetAll(payload: any) {
    const interfaceId = payload.interfaceId
    const url = `/api/station/reset-errors?interfaceId=${interfaceId}`
    return axiosClient.get(url)
  },
  resetId(payload: any) {
    const interfaceId = payload.interfaceId
    const id = payload.id
    const url = `/api/station/reset-error-by-id?interfaceId=${interfaceId}&id=${id}`
    return axiosClient.get(url)
  },
  delete(interfaceId: string, id: string) {
    const url = `/api/station/?interfaceId=${interfaceId}&id=${id}`
    return axiosClient.delete(url)
  },
  update(payload: UpdateStation) {
    const url = '/api/station'
    return axiosClient.put(url, payload)
  },
  getStationListByInterface(interfaceId: string, searchParams: SearchStation) {
    const url = `/api/station/${interfaceId}/getlistprogrambystationasync`
    return axiosClient.get(url + queryStringify(searchParams))
  },
  getListWithDetail(
    interfaceId: number,
    params?: SearchStation,
    filterParams?: SearchParams,
  ) {
    const url =
      API.CONFIG.STATION.GET_LIST_WITH_DETAIL(interfaceId) +
      queryStringify(filterParams || {})
    return axiosClient.get<DATA_PAGINATION<SetupStation>>(url, { params })
  },

  getListWithSoilMoisture(interfaceId: number, params?: SearchParams) {
    const url = API.CONFIG.SOIL_MOISTURE.GET_LIST_WITH_DETAIL(interfaceId)
    return axiosClient.get<SetupStation>(url, { params })
  },

  getListWithSoilMoistureData(interfaceId: number, sensorId: number) {
    const url = API.CONFIG.SOIL_MOISTURE.GET_LIST_WITH_DETAIL_DATA(
      interfaceId,
      sensorId,
    )
    return axiosClient.get<SoilMoistureDetail>(url)
  },

  getListWithSensor(interfaceId: number, params?: SearchParams) {
    const url = API.CONFIG.SENSOR.GET_LIST_WITH_DETAIL(interfaceId)
    return axiosClient.get<SetupStation>(url, { params })
  },

  updateStations(data: UPDATE_STATION_DATA[]) {
    const url = API.CONFIG_SETUP.STATION.UPDATE
    return axiosClient.put(url, data)
  },

  updateSoilMoisture(data: UPDATE_SOIL_MOISTURE_DATA[]) {
    const url = API.CONFIG_SETUP.SOIL_MOISTURE.UPDATE
    return axiosClient.post(url, data)
  },

  updateFlowSensor(data: UPDATE_SOIL_MOISTURE_DATA[]) {
    const url = API.CONFIG_SETUP.FLOW_SENSOR.UPDATE
    return axiosClient.post(url, data)
  },
  offStationMultiple(payload: OffStationMultiple) {
    const url = '/api/station/off/multiple'
    return axiosClient.post(url, payload)
  },

  getStationLineUnitControl(
    interfaceId: number,
    addressLineUnit: number,
    twiId?: number,
  ) {
    var url = `/api/lineunit/${interfaceId}/${addressLineUnit}`
    url += twiId ? `?twiId=${twiId}` : ''
    return axiosClient.get(url)
  },
  getStationLineUnitDecoder(params: {
    interfaceId: number
    lineId: number
    ioId: number
    twiId?: number
  }) {
    var url = `/api/lineunit/decoder-readings`
    return axiosClient.get<DecoderReading>(url, { params })
  },
  resetLineUnits({ interfaceId, verificationToken }: ResetLineUnits) {
    const url = `/api/lineunit/reset?interfaceId=${interfaceId}`
    return axiosClient.post(url, interfaceId, {
      headers: { 'X-Step-Up-Verification': verificationToken },
    })
  },
  applyObervedFlow(payload: ApplyObservedFlow) {
    const url = '/api/station/apply-observed-flow'
    return axiosClient.post(url, payload)
  },
}

export default stationDataConfigApi
